import { Dispatch, useMemo, useRef, useState } from 'react'
import { PiCaretRight, PiCircleNotch } from 'react-icons/pi'
import { Alert } from 'components/FbUI/Alert'
import FbButton from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import { FloatingTooltip } from 'components/Tooltip/FloatingTooltip'
import { MappingSelect, MappingGroup } from './MappingSelect'
import Checkbox from 'components/Checkbox/checkbox'
import { useMountEffect } from 'hooks/useMountEffect'
import {
  columnOptions,
  getColumnOption,
  AUTO_MATCH_PATTERNS,
} from 'features/importerv2/columnMappingShared'
import { autoMatchWithConflictResolution } from 'features/importerv2/auto-match'
import { setHeaderMapping } from 'features/importerv2/importDataTransforms'
import { ImportData } from 'features/importerv2/importer-types'
import {
  createImportPreview,
  fetchImportPreviewData,
  rePrepareImportPreview,
} from 'features/importerv2/importer-api'
import { toRowsJSON, fromRowsJSON } from 'features/importerv2/ImporterViewModel'
import { CONTACT_IMPORT_FIELD_KEYS } from '../contactImportFields'
import { pollUntilIdle } from '../pollImport'
import { WizardState, WizardAction } from '../wizardState'
import { SampleDataPreview } from './MapColumnsPreview'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { collectCustomColumns } from '../customColumns'

// ---------------------------------------------------------------------------
// Constants derived at module level (no runtime overhead per render)
// ---------------------------------------------------------------------------

type ContactFieldKey = (typeof CONTACT_IMPORT_FIELD_KEYS)[number]

const CONTACT_FIELD_KEY_SET: ReadonlySet<string> = new Set<string>(
  CONTACT_IMPORT_FIELD_KEYS
)

/**
 * Auto-match patterns restricted to the contact-import field set.
 * Prevents auto-matching from ever proposing a non-contact field (e.g.
 * deal_sales_stage, tag_name, company_notes) when parsing a contact CSV.
 */
const FILTERED_PATTERNS: Record<string, string[]> = Object.fromEntries(
  Object.entries(AUTO_MATCH_PATTERNS).filter(([key]) =>
    CONTACT_FIELD_KEY_SET.has(key)
  )
)

/**
 * Grouped target options for the "Map to existing" dropdown, restricted to the
 * contact-import field set. Identity fields first, then Company.
 * (No CRM/deal fields are in scope for the standalone contacts importer.)
 */
const TARGET_GROUPS: { label: string; keys: ContactFieldKey[] }[] = [
  {
    label: 'Identity',
    keys: [
      'contact_first_name',
      'contact_last_name',
      'contact_full_name',
      'contact_title',
      'contact_phone',
      'contact_work_email',
      'contact_personal_email',
      'contact_generic_email',
      'contact_linkedin_url',
      'contact_label',
    ],
  },
  {
    label: 'Company',
    keys: ['company_name'],
  },
]

/**
 * Grouped options for the "Map To" DS2026 select (Identity / Company sections).
 * Derived once from TARGET_GROUPS.
 */
const MAP_TO_GROUPS: MappingGroup[] = TARGET_GROUPS.map((group) => ({
  label: group.label,
  options: group.keys.map((key) => ({
    value: key,
    label: columnOptions[key].name,
  })),
}))

// CSV Column | First Bite Column | Include | (preview)
const AUTO_GRID = 'grid-cols-[16rem_minmax(0,1fr)_80px_48px]'
// CSV Column | Action | Map to | (preview)
const UNMATCHED_GRID = 'grid-cols-[16rem_14rem_minmax(0,1fr)_48px]'

/** Field key sentinel meaning "not imported". */
const IGNORE = 'ignore'

/**
 * Stable, order-sensitive signature of a column mapping. Two mappings produce
 * the same signature iff every (original, mapped) pair matches in the same
 * order. Used to decide whether Next can reuse the existing prepared preview or
 * must re-prepare it because the mapping changed.
 */
function mappingSignature(
  headers: { original: string; mapped: string }[]
): string {
  return JSON.stringify(headers.map((h) => [h.original, h.mapped]))
}

/**
 * CSV-header pill that truncates to the fixed first-column width and — only when
 * the name is actually clipped — reveals the full name in a dark tooltip on
 * hover. Truncation is measured once on mount (the column width is fixed), and a
 * fixed `max-w` keeps the clip working even inside the tooltip's wrapper.
 */
function TruncatingPill({
  label,
  className,
}: {
  label: string
  className: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [truncated, setTruncated] = useState(false)

  useMountEffect(() => {
    const el = ref.current
    if (el) setTruncated(el.scrollWidth > el.clientWidth)
  })

  const pill = (
    <span ref={ref} className={className}>
      {label}
    </span>
  )

  if (!truncated) return pill
  return (
    <FloatingTooltip text={label} theme="dark">
      {pill}
    </FloatingTooltip>
  )
}

// ---------------------------------------------------------------------------
// MapColumnsStep
// ---------------------------------------------------------------------------

interface MapColumnsStepProps {
  state: WizardState
  dispatch: Dispatch<WizardAction>
}

/**
 * Step 2 of the contact import wizard.
 *
 * Auto-matches headers once on mount, then partitions columns into two
 * sections: "Auto-Matched" (columns auto-match resolved to a real field) and
 * "Unmatched" (everything else). Auto-matched rows expose an Include checkbox;
 * unmatched rows expose a Skip / Map-to-existing action. Both sections expose a
 * sample-data preview. Excluding a column (uncheck Include, or Skip action)
 * sets that header's `mapped` to 'ignore'; the required-field gate and the
 * create-preview-on-Next logic operate on the effective (included) mappings.
 */
export function MapColumnsStep({ state, dispatch }: MapColumnsStepProps) {
  const importData = state.importData
  const featureFlags = useFeatureFlagService()
  const customColumnsEnabled = featureFlags.enableAiCustomColumns
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  /** Which column's preview popover is open (one at a time). */
  const [openPreview, setOpenPreview] = useState<string | null>(null)
  /**
   * For each auto-matched column, the field it was assigned on mount. Used both
   * to partition the sections and to restore the mapping when an excluded
   * column is re-included. Columns absent from this map are "unmatched".
   */
  const [autoMappedFields, setAutoMappedFields] = useState<
    Record<string, string>
  >({})
  /**
   * Unmatched columns the user switched to "Map to existing" but has not yet
   * picked a target for (so `mapped` is still 'ignore' but the dropdown shows).
   */
  const [pendingMapColumns, setPendingMapColumns] = useState<Set<string>>(
    () => new Set()
  )
  /**
   * Unmatched columns where the user selected "Create custom column" action.
   * Mutually exclusive with the "Map to existing" action — switching away
   * from "custom" removes the header from this set and clears the name.
   *
   * Lazy-initialized from `state.customColumns` so Back→Next round-trips
   * preserve the user's selections. Only rehydrates headers that still exist
   * in importData with mapped === IGNORE (custom/skipped columns) — any header
   * that was since mapped to a standard field is excluded. This check uses
   * importData directly at init time (autoMappedFields is not yet populated),
   * which is equivalent because standard-mapped headers have mapped !== IGNORE.
   */
  const [customActionHeaders, setCustomActionHeaders] = useState<Set<string>>(
    () => {
      if (!importData || state.customColumns.length === 0) return new Set()
      const ignoredHeaders = new Set(
        importData.headers
          .filter((h) => h.mapped === IGNORE)
          .map((h) => h.original)
      )
      return new Set(
        state.customColumns
          .map((c) => c.header)
          .filter((h) => ignoredHeaders.has(h))
      )
    }
  )
  /**
   * Per-header custom column names entered by the user when action is "custom".
   * Only headers in `customActionHeaders` that are still unmapped (not mapped
   * to a standard field) are collected on Next.
   *
   * Lazy-initialized from `state.customColumns` with the same eligibility
   * filter as `customActionHeaders` above.
   */
  const [customNamesByHeader, setCustomNamesByHeader] = useState<
    Record<string, string>
  >(() => {
    if (!importData || state.customColumns.length === 0) return {}
    const ignoredHeaders = new Set(
      importData.headers
        .filter((h) => h.mapped === IGNORE)
        .map((h) => h.original)
    )
    return Object.fromEntries(
      state.customColumns
        .filter((c) => ignoredHeaders.has(c.header))
        .map((c) => [c.header, c.name])
    )
  })

  // ---------------------------------------------------------------------------
  // Auto-match on mount (one-time sync — useMountEffect is the approved path)
  // ---------------------------------------------------------------------------
  useMountEffect(() => {
    if (!importData) return

    const allIgnored = importData.headers.every((h) => h.mapped === IGNORE)

    if (allIgnored) {
      const autoMapped = autoMatchWithConflictResolution(
        importData.headers,
        FILTERED_PATTERNS
      )
      const autoFields: Record<string, string> = {}
      autoMapped.forEach((h) => {
        if (h.mapped !== IGNORE) autoFields[h.original] = h.mapped
      })
      setAutoMappedFields(autoFields)
      const updatedData: ImportData = { ...importData, headers: autoMapped }
      dispatch({ type: 'SET_IMPORT_DATA', data: updatedData })
    } else {
      // Already mapped (e.g. back-nav). Treat currently-mapped columns as the
      // auto-matched section so the partition stays stable.
      const autoFields: Record<string, string> = {}
      importData.headers.forEach((h) => {
        if (h.mapped !== IGNORE) autoFields[h.original] = h.mapped
      })
      setAutoMappedFields(autoFields)
    }
  })

  // ---------------------------------------------------------------------------
  // Section partition (stable across include/skip toggles)
  // ---------------------------------------------------------------------------
  const headers = useMemo(
    () => importData?.headers ?? [],
    [importData?.headers]
  )

  const { autoColumns, unmatchedColumns } = useMemo(() => {
    const auto: typeof headers = []
    const unmatched: typeof headers = []
    headers.forEach((h) => {
      if (h.original in autoMappedFields) auto.push(h)
      else unmatched.push(h)
    })
    return { autoColumns: auto, unmatchedColumns: unmatched }
  }, [headers, autoMappedFields])

  // ---------------------------------------------------------------------------
  // Derived: effective (included) mapped set + canProceed gate
  // ---------------------------------------------------------------------------
  const mapped = useMemo(
    () => new Set((importData?.headers ?? []).map((h) => h.mapped)),
    [importData?.headers]
  )

  // At least a first name (or a full name) must be mapped. Last name is optional.
  const hasName =
    mapped.has('contact_full_name') || mapped.has('contact_first_name')
  const hasCompany = mapped.has('company_name')
  const hasEmail = (
    [
      'contact_work_email',
      'contact_personal_email',
      'contact_generic_email',
    ] as const
  ).some((k) => mapped.has(k))
  const canProceed = hasName && hasCompany && hasEmail
  // A header set to "Create custom campaign column" needs a non-blank name —
  // otherwise it's silently dropped at import. Block Next until it's named.
  const hasUnnamedCustomColumn = Array.from(customActionHeaders).some(
    (h) => !(customNamesByHeader[h] ?? '').trim()
  )
  // A header switched to "Map to existing" but with no target picked yet would
  // be silently skipped — flag it and block Next until a column is chosen.
  const hasUnselectedMapColumn = Array.from(pendingMapColumns).some(
    (h) =>
      (importData?.headers.find((header) => header.original === h)?.mapped ??
        IGNORE) === IGNORE
  )

  const autoIncludedCount = autoColumns.filter(
    (h) => h.mapped !== IGNORE
  ).length

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  function updateMapping(originalHeader: string, newValue: string) {
    if (!importData) return
    const updated = setHeaderMapping(importData, originalHeader, newValue)
    dispatch({ type: 'SET_IMPORT_DATA', data: updated })
  }

  function toggleAutoInclude(originalHeader: string, included: boolean) {
    // Excluding sets mapped to 'ignore'; re-including restores the auto field.
    updateMapping(
      originalHeader,
      included ? IGNORE : (autoMappedFields[originalHeader] ?? IGNORE)
    )
  }

  /**
   * Switch the action for an unmatched header between skip / map-to-existing /
   * create-custom. Keeps `pendingMapColumns`, `customActionHeaders`, and
   * `customNamesByHeader` in sync, and resets the header's `mapped` value to
   * IGNORE when leaving "map to existing".
   */
  function setUnmatchedAction(
    originalHeader: string,
    action: 'skip' | 'map' | 'custom'
  ) {
    setPendingMapColumns((prev) => {
      const next = new Set(prev)
      if (action === 'map') next.add(originalHeader)
      else next.delete(originalHeader)
      return next
    })
    setCustomActionHeaders((prev) => {
      const next = new Set(prev)
      if (action === 'custom') next.add(originalHeader)
      else {
        next.delete(originalHeader)
        // Clear the saved name when leaving custom mode
        setCustomNamesByHeader((names) => {
          const updated = { ...names }
          delete updated[originalHeader]
          return updated
        })
      }
      return next
    })
    if (action === 'skip' || action === 'custom') {
      updateMapping(originalHeader, IGNORE)
    }
  }

  function setUnmatchedTarget(originalHeader: string, fieldKey: string) {
    updateMapping(originalHeader, fieldKey === '' ? IGNORE : fieldKey)
  }

  /** Record the custom column name the user typed for an unmatched header. */
  function setCustomName(originalHeader: string, name: string) {
    setCustomNamesByHeader((prev) => ({ ...prev, [originalHeader]: name }))
  }

  async function handleNext() {
    if (!state.importData || !state.file) return

    // Collect custom columns only for headers that are still unmapped (not
    // mapped to a standard field) and have the "custom" action selected.
    if (customColumnsEnabled) {
      const unmappedCustomNames: Record<string, string> = {}
      for (const header of customActionHeaders) {
        // Only include if the header is still set to IGNORE (not mapped to a
        // standard field). If the user switched to "map" and back, the header
        // would have been removed from customActionHeaders already.
        const headerObj = state.importData.headers.find(
          (h) => h.original === header
        )
        if (headerObj?.mapped === IGNORE && customNamesByHeader[header]) {
          unmappedCustomNames[header] = customNamesByHeader[header]
        }
      }
      dispatch({
        type: 'SET_CUSTOM_COLUMNS',
        customColumns: collectCustomColumns(unmappedCustomNames),
      })
    }

    const sig = mappingSignature(state.importData.headers)

    // Case 1 — Reuse: the mapping is unchanged since the last prepare, so the
    // existing preview's prepared data is still valid. Skip the backend
    // entirely and keep any edits the user made on the Validate step (those
    // live in state.importData).
    if (state.importNumber != null && sig === state.preparedMappingSig) {
      dispatch({ type: 'SET_STEP', step: 'validate' })
      return
    }

    setIsCreating(true)
    setCreateError(null)
    try {
      if (state.importNumber != null) {
        // Case 2 — Re-prepare: a preview already exists but the mapping
        // changed. Re-run prepare on the SAME import_number (no orphan).
        // Sending the current rows preserves user-column edits; prepare
        // recomputes the internal columns for the new mapping.
        await rePrepareImportPreview(
          state.importNumber,
          toRowsJSON(state.importData)
        )
        await pollUntilIdle(state.importNumber)
        const prepared = await fetchImportPreviewData(state.importNumber)
        dispatch({ type: 'SET_IMPORT_DATA', data: fromRowsJSON(prepared.data) })
        dispatch({ type: 'SET_PREPARED_MAPPING_SIG', sig })
        dispatch({ type: 'SET_STEP', step: 'validate' })
        return
      }

      // Case 3 — Create: first time through, no preview exists yet.
      const created = await createImportPreview({
        import_data: {
          data: toRowsJSON(state.importData),
          raw_data: await state.file.text(),
          name: state.file.name,
          skip_matching: true,
        },
      })
      // Poll until the Celery prepare_df task completes (adds ROW_ID /
      // COMPANY_HASH to rows) before fetching the prepared data. Without
      // this, a GET that lands before the task runs returns rows without
      // internal columns, breaking all downstream steps.
      await pollUntilIdle(created.import_number)
      const prepared = await fetchImportPreviewData(created.import_number)
      dispatch({
        type: 'SET_IMPORT_NUMBER',
        importNumber: created.import_number,
      })
      dispatch({ type: 'SET_IMPORT_DATA', data: fromRowsJSON(prepared.data) })
      dispatch({ type: 'SET_PREPARED_MAPPING_SIG', sig })
      dispatch({ type: 'SET_STEP', step: 'validate' })
    } catch {
      setCreateError('Failed to create import. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  const rows = (importData?.rows ?? []) as Record<string, unknown>[]

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0 px-8 py-6 flex flex-col gap-6">
        {/* Required-field gate banner */}
        {!canProceed && headers.length > 0 && (
          <Alert
            color="warning"
            description={
              <>
                <span className="font-semibold">Required:</span> Map a First
                Name (or Full Name), a Company Name, and at least one Email
                column to continue.
              </>
            }
          />
        )}

        {headers.length === 0 ? (
          <p className="text-sm text-fg-secondary">No CSV headers found.</p>
        ) : (
          <>
            {/* ── Auto-Matched Columns ─────────────────────────────────── */}
            {autoColumns.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-fg flex items-center gap-2">
                    <span className="inline-flex items-center justify-center size-5 rounded-full bg-neutral-100 text-neutral-700 text-xs font-bold">
                      {autoColumns.length}
                    </span>
                    Auto-Matched Columns
                  </h3>
                  <div className="flex items-center gap-3">
                    {autoIncludedCount < autoColumns.length && (
                      <span className="text-xs text-fg-secondary">
                        <span className="text-fg font-semibold">
                          {autoIncludedCount}
                        </span>
                        /{autoColumns.length} included
                      </span>
                    )}
                    <span className="text-xs text-fg-secondary">
                      First Bite matched these automatically
                    </span>
                  </div>
                </div>

                <div className="rounded-xl border border-line overflow-hidden">
                  <div
                    className={cn(
                      'grid gap-3 px-4 py-2.5 bg-surface-sunken border-b border-line items-center',
                      AUTO_GRID
                    )}
                  >
                    <span className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
                      CSV Column
                    </span>
                    <span className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
                      First Bite Column
                    </span>
                    <span className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide text-center">
                      Include
                    </span>
                    <span className="text-center text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
                      View
                    </span>
                  </div>

                  {autoColumns.map((header, idx) => {
                    const isIncluded = header.mapped !== IGNORE
                    const field = getColumnOption(
                      autoMappedFields[header.original]
                    )
                    return (
                      <div
                        key={header.original}
                        className={cn(
                          'grid gap-3 px-4 py-3 items-center transition-colors',
                          AUTO_GRID,
                          idx < autoColumns.length - 1 &&
                            'border-b border-line',
                          !isIncluded && 'bg-surface-sunken'
                        )}
                      >
                        <div className="min-w-0">
                          <TruncatingPill
                            label={header.original}
                            className={cn(
                              // max-w is fixed to the first-column width so the
                              // clip survives the tooltip wrapper (see 16rem col).
                              'inline-block px-2 py-0.5 rounded text-xs font-mono truncate max-w-[15rem] bg-surface-sunken',
                              isIncluded ? 'text-fg' : 'text-fg-secondary'
                            )}
                          />
                        </div>

                        <span
                          className={cn(
                            'text-sm font-medium truncate',
                            isIncluded ? 'text-fg' : 'text-fg-secondary'
                          )}
                        >
                          {field?.name}
                        </span>

                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={isIncluded}
                            onChange={() =>
                              toggleAutoInclude(header.original, isIncluded)
                            }
                            aria-label={`Include column "${header.original}"`}
                          />
                        </div>

                        <div className="flex items-center justify-center">
                          <SampleDataPreview
                            csvHeader={header.original}
                            mappedLabel={isIncluded ? field?.name : undefined}
                            isNotIncluded={!isIncluded}
                            rows={rows}
                            isActive={openPreview === header.original}
                            open={openPreview === header.original}
                            onOpenChange={(o) =>
                              setOpenPreview(o ? header.original : null)
                            }
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}

            {/* ── Unmatched Columns ────────────────────────────────────── */}
            {unmatchedColumns.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-fg flex items-center gap-2">
                    <span className="inline-flex items-center justify-center size-5 rounded-full bg-warning-bg-subtle text-warning-strong text-xs font-bold">
                      {unmatchedColumns.length}
                    </span>
                    Unmatched Columns
                  </h3>
                  <span className="text-xs text-fg-secondary">
                    Choose what to do with each
                  </span>
                </div>

                <div className="rounded-xl border border-line overflow-hidden">
                  <div
                    className={cn(
                      'grid gap-3 px-4 py-2.5 bg-surface-sunken border-b border-line items-center',
                      UNMATCHED_GRID
                    )}
                  >
                    <span className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
                      CSV Column
                    </span>
                    <span className="text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
                      Action
                    </span>
                    <span className="pl-3 text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
                      Map To
                    </span>
                    <span className="text-center text-xs font-semibold text-fg-tertiary uppercase tracking-wide">
                      View
                    </span>
                  </div>

                  {unmatchedColumns.map((header, idx) => {
                    const isMapped = header.mapped !== IGNORE
                    const isMapAction =
                      isMapped || pendingMapColumns.has(header.original)
                    const isCustomAction = customActionHeaders.has(
                      header.original
                    )
                    const mappedField = isMapped
                      ? getColumnOption(header.mapped)
                      : undefined
                    const currentAction = isMapAction
                      ? 'map'
                      : isCustomAction
                        ? 'custom'
                        : 'skip'
                    return (
                      <div
                        key={header.original}
                        className={cn(
                          // items-start (not center) so a taller cell — the custom
                          // input + validation message — only extends the row
                          // downward instead of re-centering the other cells.
                          'grid gap-3 px-4 py-3 items-start transition-colors',
                          UNMATCHED_GRID,
                          idx < unmatchedColumns.length - 1 &&
                            'border-b border-line'
                        )}
                      >
                        <div className="min-w-0">
                          <TruncatingPill
                            label={header.original}
                            className="inline-block px-2 py-0.5 rounded text-xs font-mono truncate max-w-[15rem] bg-warning-bg-subtle text-warning-strong"
                          />
                        </div>

                        {/* Action dropdown */}
                        <MappingSelect
                          value={currentAction}
                          onChange={(v) =>
                            setUnmatchedAction(
                              header.original,
                              v as 'skip' | 'map' | 'custom'
                            )
                          }
                          ariaLabel={`Action for column "${header.original}"`}
                          placeholder="Select an action"
                          groups={[
                            {
                              options: [
                                { value: 'skip', label: 'Skip / Ignore' },
                                { value: 'map', label: 'Map to existing' },
                                ...(customColumnsEnabled
                                  ? [
                                      {
                                        value: 'custom',
                                        label: 'Create custom campaign column',
                                      },
                                    ]
                                  : []),
                              ],
                            },
                          ]}
                        />

                        {/* Target dropdown / custom name input / skip hint */}
                        <div className="min-w-0 pl-3">
                          {isMapAction ? (
                            // Error below the select (row is items-start, so it
                            // only grows downward) when no target is picked yet.
                            <div className="flex flex-col gap-1">
                              <MappingSelect
                                value={isMapped ? header.mapped : ''}
                                onChange={(v) =>
                                  setUnmatchedTarget(header.original, v)
                                }
                                ariaLabel={`Map column "${header.original}" to`}
                                placeholder="Select a column…"
                                searchable
                                searchPlaceholder="Search columns…"
                                noResultsText="No columns found"
                                groups={MAP_TO_GROUPS}
                                invalid={!isMapped}
                              />
                              {!isMapped && (
                                <span className="text-xs font-medium text-error-strong">
                                  Select a column to map to.
                                </span>
                              )}
                            </div>
                          ) : isCustomAction ? (
                            // Error sits below the input; the row uses items-start
                            // so showing it only grows the row downward and the
                            // other cells stay put (no re-centering).
                            <div className="flex flex-col gap-1">
                              <input
                                type="text"
                                placeholder="Column name…"
                                value={
                                  customNamesByHeader[header.original] ?? ''
                                }
                                onChange={(e) =>
                                  setCustomName(header.original, e.target.value)
                                }
                                aria-label={`Custom column name for "${header.original}"`}
                                className={cn(
                                  'w-full rounded-lg border bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand/30',
                                  (
                                    customNamesByHeader[header.original] ?? ''
                                  ).trim()
                                    ? 'border-line-form focus:border-brand'
                                    : 'border-error-strong'
                                )}
                              />
                              {!(
                                customNamesByHeader[header.original] ?? ''
                              ).trim() && (
                                <span className="text-xs font-medium text-error-strong">
                                  Enter a name for this column.
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm italic text-fg-secondary">
                              Will not be imported
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-center">
                          <SampleDataPreview
                            csvHeader={header.original}
                            mappedLabel={
                              isCustomAction
                                ? customNamesByHeader[
                                    header.original
                                  ]?.trim() || 'Custom column'
                                : mappedField?.name
                            }
                            // Custom columns ARE imported (as a custom campaign
                            // column), so only skip/unmapped reads "won't import".
                            isNotIncluded={!isMapped && !isCustomAction}
                            rows={rows}
                            isActive={openPreview === header.original}
                            open={openPreview === header.original}
                            onOpenChange={(o) =>
                              setOpenPreview(o ? header.original : null)
                            }
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-8 py-4 border-t border-line bg-surface flex items-center justify-between shrink-0">
        <FbButton
          variant="secondary"
          onClick={() => dispatch({ type: 'SET_STEP', step: 'upload' })}
        >
          Back
        </FbButton>

        <div className="flex items-center gap-3">
          {createError !== null && (
            <p role="alert" className="text-sm text-error-strong">
              {createError}
            </p>
          )}
          <FbButton
            variant="primary"
            disabled={
              !canProceed ||
              isCreating ||
              hasUnnamedCustomColumn ||
              hasUnselectedMapColumn
            }
            onClick={handleNext}
          >
            {isCreating ? (
              <>
                <PiCircleNotch className="size-4 animate-spin" />
                Creating import…
              </>
            ) : (
              <>
                Validate
                <PiCaretRight className="size-4" />
              </>
            )}
          </FbButton>
        </div>
      </div>
    </div>
  )
}
